import { safeParseSchemaWithSentry } from "@/features/Sentry/utils";
import { StreamEvent, StreamEventSchema, TextEventSchema } from "../../event";
import { updateUrlWithConversationId } from "../urlManagement";

/**
 * Processes a single Server-Sent Event (SSE) string into a structured event object
 * @param event - SSE event string
 * @returns Processed event data or null if invalid
 */
export const processSSEEvent = (event: string): StreamEvent | null => {
  if (!event.trim() || !event.startsWith("data: ")) return null;

  try {
    const jsonData = JSON.parse(event.substring(6));

    if (jsonData && typeof jsonData.text === "string") {
      const textResult = safeParseSchemaWithSentry(TextEventSchema, jsonData);
      if (textResult.success) {
        return textResult.data;
      }
    }

    const result = StreamEventSchema.safeParse(jsonData);
    if (result.success) {
      return result.data;
    }

    console.warn("Invalid event format:", result.error, jsonData);
    return null;
  } catch (e) {
    console.error("JSON parsing error:", e, "for event:", event);
    return null;
  }
};

/**
 * Decodes a binary chunk of data to text using the provided decoder
 * @param value - Binary chunk from stream
 * @param decoder - TextDecoder instance
 * @returns Decoded text
 */
export const decodeChunk = (
  value: Uint8Array,
  decoder: TextDecoder,
): string => {
  return decoder.decode(value, { stream: true });
};

/**
 * Splits a buffer into complete events and returns remaining buffer content
 * @param buffer - Current buffer content
 * @returns Array of complete events and the remaining buffer
 */
export const extractEvents = (buffer: string): [string[], string] => {
  const events = buffer.split("\n\n");
  const remainingBuffer = events.pop() || "";
  return [events, remainingBuffer];
};

/**
 * Releases the stream reader lock to properly cleanup resources
 * @param reader - Stream reader to release
 */
export const cleanupStream = (
  reader: ReadableStreamDefaultReader<Uint8Array>,
): void => {
  reader.releaseLock();
};

/**
 * Determines if a buffer contains initialization data with conversation ID
 * @param buffer - Buffer content to check
 * @returns True if buffer contains initialization data
 */
export const hasInitializationData = (buffer: string): boolean => {
  return (
    buffer.includes('"type": "init"') && buffer.includes('"conversation_id":')
  );
};

/**
 * Extracts the initialization event from a buffer of SSE events
 * @param buffer - Buffer containing SSE events
 * @returns The initialization event if found, null otherwise
 */
export const findInitEvent = (buffer: string): string | null => {
  const events = buffer.split("\n\n");
  const initEvent = events.find(
    (e) => e.includes('"type": "init"') && e.includes('"conversation_id":'),
  );

  return initEvent || null;
};

/**
 * Parses initialization data from an SSE event string
 * @param initEvent - SSE event string containing initialization data
 * @returns Parsed initialization data or null if parsing fails
 */
export const parseInitEvent = (initEvent: string): any | null => {
  try {
    const dataStart = initEvent.indexOf("data: ") + 6;
    return JSON.parse(initEvent.substring(dataStart));
  } catch (err) {
    console.error("Error parsing init event:", err);
    return null;
  }
};

/**
 * Extracts and converts conversation ID from initialization data
 * @param initData - Parsed initialization data
 * @returns Conversation ID as number or null if invalid
 */
export const extractConversationIdFromInit = (initData: any): number | null => {
  if (!initData || !initData.conversation_id) return null;

  try {
    return typeof initData.conversation_id === "number"
      ? initData.conversation_id
      : parseInt(String(initData.conversation_id), 10);
  } catch (err) {
    console.error("Error converting conversation_id to number:", err);
    return null;
  }
};

/**
 * Checks if buffer content is in valid SSE format
 * @param buffer - Buffer to check
 * @returns True if buffer appears to be valid SSE format
 */
export const isValidSSEFormat = (buffer: string): boolean => {
  return buffer.includes("data: ");
};

/**
 * Processes the first chunk of an SSE response to extract conversation ID and update URL
 * @param buffer - Current buffer content
 * @param currentConversationId - Current conversation ID if any
 * @returns Extracted conversation ID if found
 */
export const processFirstChunk = (
  buffer: string,
  currentConversationId: number | null,
): number | null => {
  let receivedConversationId = null;

  if (hasInitializationData(buffer)) {
    const initEvent = findInitEvent(buffer);

    if (initEvent) {
      const initData = parseInitEvent(initEvent);
      receivedConversationId = extractConversationIdFromInit(initData);

      if (!currentConversationId && receivedConversationId) {
        updateUrlWithConversationId(receivedConversationId);
      }
    }
  }

  if (!isValidSSEFormat(buffer)) {
    console.warn("Response format doesn't appear to be SSE. Content:", buffer);
  }

  return receivedConversationId;
};
